import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// project imports
import Loader from 'components/Loader';
import axios, { setAuthToken } from 'utils/axios';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  KULLANICI_TIPI_KEY,
  AD_SOYAD_KEY,
  AKTIF_SITE_KEY,
  DAIRELER_KEY,
  SITELER_KEY,
  PAROLA_DEGISIKLIGI_GEREKLI_KEY,
  clearAuthStorage
} from 'utils/authStorage';

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const initialState = {
  isInitialized: false,
  isLoggedIn: false,
  kullaniciTipi: null,
  adSoyad: null,
  aktifSite: null,
  siteler: null,
  parolaDegisikligiGerekli: false
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const { isLoggedIn, kullaniciTipi, adSoyad, aktifSite, parolaDegisikligiGerekli } = action.payload;
      return { ...state, isInitialized: true, isLoggedIn, kullaniciTipi, adSoyad, aktifSite, parolaDegisikligiGerekli };
    }
    case 'AWAIT_SITE_SELECTION': {
      const { kullaniciTipi, adSoyad, siteler, parolaDegisikligiGerekli } = action.payload;
      return { ...state, isLoggedIn: false, kullaniciTipi, adSoyad, siteler, aktifSite: null, parolaDegisikligiGerekli };
    }
    case 'SITE_SELECTED': {
      const { aktifSite, kullaniciTipi, adSoyad, parolaDegisikligiGerekli } = action.payload;
      return {
        ...state,
        isLoggedIn: true,
        aktifSite,
        kullaniciTipi: kullaniciTipi ?? state.kullaniciTipi,
        adSoyad: adSoyad ?? state.adSoyad,
        parolaDegisikligiGerekli: parolaDegisikligiGerekli ?? state.parolaDegisikligiGerekli,
        siteler: null
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isLoggedIn: false,
        kullaniciTipi: null,
        adSoyad: null,
        aktifSite: null,
        siteler: null,
        parolaDegisikligiGerekli: false
      };
    }
    case 'PAROLA_DEGISTIRILDI': {
      return { ...state, parolaDegisikligiGerekli: false };
    }
    default: {
      return state;
    }
  }
};

export const JWTContext = createContext(null);

function persistSession({ access, refresh, kullaniciTipi, adSoyad, parolaDegisikligiGerekli }) {
  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  localStorage.setItem(KULLANICI_TIPI_KEY, kullaniciTipi);
  localStorage.setItem(AD_SOYAD_KEY, adSoyad || '');
  localStorage.setItem(PAROLA_DEGISIKLIGI_GEREKLI_KEY, String(Boolean(parolaDegisikligiGerekli)));
  setAuthToken(access);
}

function clearSession() {
  clearAuthStorage();
  setAuthToken(null);
}

export function JWTProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const init = () => {
      try {
        const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        const kullaniciTipi = localStorage.getItem(KULLANICI_TIPI_KEY);
        const adSoyad = localStorage.getItem(AD_SOYAD_KEY);
        const aktifSiteRaw = localStorage.getItem(AKTIF_SITE_KEY);
        const parolaDegisikligiGerekli = localStorage.getItem(PAROLA_DEGISIKLIGI_GEREKLI_KEY) === 'true';

        if (accessToken && aktifSiteRaw) {
          setAuthToken(accessToken);
          dispatch({
            type: 'INIT',
            payload: { isLoggedIn: true, kullaniciTipi, adSoyad, aktifSite: JSON.parse(aktifSiteRaw), parolaDegisikligiGerekli }
          });
        } else {
          clearSession();
          dispatch({
            type: 'INIT',
            payload: { isLoggedIn: false, kullaniciTipi: null, adSoyad: null, aktifSite: null, parolaDegisikligiGerekli: false }
          });
        }
      } catch {
        clearSession();
        dispatch({
          type: 'INIT',
          payload: { isLoggedIn: false, kullaniciTipi: null, adSoyad: null, aktifSite: null, parolaDegisikligiGerekli: false }
        });
      }
    };

    init();
  }, []);

  const login = async (kullaniciTipi, kullaniciAdi, parola) => {
    const { data } = await axios.post('auth/login/', {
      kullanici_tipi: kullaniciTipi,
      kullanici_adi: kullaniciAdi,
      parola
    });

    persistSession({
      access: data.access,
      refresh: data.refresh,
      kullaniciTipi: data.kullanici_tipi,
      adSoyad: data.ad_soyad,
      parolaDegisikligiGerekli: data.parola_degisikligi_gerekli
    });
    // Faz 3 (Uç Durum 10 — kısmi çözüm): çoklu daireli Bina Sakini'nin login-time
    // "Daire Değiştir" ekranında kullanabilmesi için ham liste saklanır; JWTContext
    // reducer akışı (site seçimi) buna dokunmadan aynı şekilde çalışmaya devam eder.
    if (data.daireler?.length > 1) {
      localStorage.setItem(DAIRELER_KEY, JSON.stringify(data.daireler));
    } else {
      localStorage.removeItem(DAIRELER_KEY);
    }

    if (!data.siteler?.length) {
      clearSession();
      throw new Error('Yetkili olduğunuz bir site bulunamadı.');
    }

    // Login sonrası header'dan "Site Değiştir" için saklanır (Daire Değiştir ile
    // aynı desen); SITE_SELECTED sonrası context state'i temizlenir ama liste
    // localStorage'da kalıcı olarak durur.
    if (data.siteler.length > 1) {
      localStorage.setItem(SITELER_KEY, JSON.stringify(data.siteler));
    } else {
      localStorage.removeItem(SITELER_KEY);
    }

    if (data.siteler.length === 1) {
      try {
        await selectSite(data.siteler[0], data.kullanici_tipi, data.ad_soyad, data.parola_degisikligi_gerekli);
      } catch (error) {
        clearSession();
        throw error;
      }
      return { requiresSiteSelection: false };
    }

    dispatch({
      type: 'AWAIT_SITE_SELECTION',
      payload: {
        kullaniciTipi: data.kullanici_tipi,
        adSoyad: data.ad_soyad,
        siteler: data.siteler,
        parolaDegisikligiGerekli: data.parola_degisikligi_gerekli
      }
    });
    return { requiresSiteSelection: true };
  };

  const selectSite = async (site, kullaniciTipi, adSoyad, parolaDegisikligiGerekli) => {
    await axios.post('auth/aktif-site/', { site_id: site.id });
    localStorage.setItem(AKTIF_SITE_KEY, JSON.stringify(site));
    if (adSoyad) localStorage.setItem(AD_SOYAD_KEY, adSoyad);
    dispatch({ type: 'SITE_SELECTED', payload: { aktifSite: site, kullaniciTipi, adSoyad, parolaDegisikligiGerekli } });
  };

  const logout = () => {
    clearSession();
    dispatch({ type: 'LOGOUT' });
  };

  const parolaDegisikligiTamamlandi = () => {
    localStorage.setItem(PAROLA_DEGISIKLIGI_GEREKLI_KEY, 'false');
    dispatch({ type: 'PAROLA_DEGISTIRILDI' });
  };

  if (!state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext.Provider value={{ ...state, login, selectSite, logout, parolaDegisikligiTamamlandi }}>{children}</JWTContext.Provider>;
}

JWTProvider.propTypes = { children: PropTypes.node };
